import type { AssetTaskStatus } from '@aikoa/api'
import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod/v4'
import { useAlert } from '~/components/GlobalAlert.tsx'
import { SectionContent } from '~/components/layout/Sections.tsx'
import { Heading1 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog.tsx'
import { Input } from '~/components/ui/input.tsx'
import { assetStore$ } from '~/data/assets/assetStore.ts'
import { useAsset, useDeleteAsset, useInvalidateAssetQueries } from '~/data/assets/useAssets.ts'
import { useUpdateAssetFromUrl } from '~/data/ingest/usePropertyUpdater.ts'
import { trpc } from '~/lib/trpc.tsx'

export function useDeleteProperty(assetId: string) {
  const { t } = useTranslations(['property', 'common'])
  const { showAlert } = useAlert()
  const router = useRouter()

  const { mutate: deleteAsset, isPending: isDeleting } = useDeleteAsset()

  const handleDelete = () => {
    showAlert({
      type: 'confirm',
      title: t(($) => $.view.deleteConfirmTitle),
      description: t(($) => $.view.deleteConfirmDescription),
      actions: [
        {
          label: t(($) => $.view.deleteButton),
          buttonVariant: 'primary',
          buttonSize: 'lg',
          onPress: ({ showAlert, closeAlert }) => {
            deleteAsset(assetId, {
              onSuccess: () => {
                assetStore$.pinnedAssets.delete(assetId)
                router.replace(`/(authenticated)`)
              },
              onError: (error) => {
                console.error(error)
                showAlert({
                  type: 'error',
                  title: t(($) => $.errors.error),
                  description: t(($) => $.errors.deleteFailed),
                })
              },
              onSettled: () => {
                closeAlert()
              },
            })
          },

          shouldClose: false,
        },
      ],
      cancelButton: {
        label: t(($) => $.common.navigation.previous),
        onPress: ({ closeAlert }) => closeAlert(),
        buttonVariant: 'ghost',
        buttonSize: 'lg',
      },
    })
  }

  return {
    handleDelete,
    isDeleting,
  }
}

const urlSchema = z.url()

export function useUpdatePropertyFromUrl(assetId: string) {
  const { t } = useTranslations(['property', 'common'])
  const { showAlert } = useAlert()
  const onInvalidateAsset = useInvalidateAssetQueries(assetId)

  const { asset, refetch: refetchAsset } = useAsset(assetId)
  const { mutate: updateAssetFromUrl, isPending } = useUpdateAssetFromUrl()

  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState(false)
  const [currentRunId, setCurrentRunId] = useState<string | null>(null)

  const assetSourceUrl = asset?.source?.sourceWebUrl ?? ''
  const [updateUrl, setUpdateUrl] = useState(assetSourceUrl)

  useEffect(() => {
    if (assetSourceUrl && assetSourceUrl !== updateUrl) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- stfu
      setUpdateUrl(assetSourceUrl)
    }
  }, [assetSourceUrl, updateUrl])

  const {
    data: runStatus,
    isFetching: isPolling,
    refetch: refetchRunStatus,
  } = useQuery(
    trpc.tasks.getRunStatus.queryOptions(
      { runId: currentRunId ?? '' },
      {
        enabled: !!currentRunId,
        refetchInterval: (query) => {
          const status = query.state.data
          return status === 'PENDING' ? 2000 : false
        },
        refetchIntervalInBackground: true,
      }
    )
  )

  const prevRunStatus = useRef<AssetTaskStatus | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!currentRunId) {
      prevRunStatus.current = runStatus ?? null
      return
    }

    if (runStatus === 'COMPLETED' && prevRunStatus.current !== 'COMPLETED') {
      void refetchAsset()
      setCurrentRunId(null)
    }

    if (runStatus === 'FAILED' && prevRunStatus.current !== 'FAILED') {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.updateFailed),
        description: t(($) => $.actions.retry),
        cancelButton: {
          label: t(($) => $.actions.close),
          buttonVariant: 'ghost',
          buttonSize: 'lg',
        },
      })

      setCurrentRunId(null)
    }

    prevRunStatus.current = runStatus ?? null
  }, [runStatus, currentRunId, refetchAsset, showAlert, t])

  const onUpdateAssetFromUrl = useCallback(
    (url: string, onDone?: () => unknown) => {
      updateAssetFromUrl(
        { assetId, url },
        {
          onSuccess: (data) => {
            if (data?.runId) {
              setCurrentRunId(data.runId)
              void refetchRunStatus()
            }

            void onInvalidateAsset?.()
          },
          onError: () => {
            showAlert({
              type: 'error',
              title: t(($) => $.errors.error),
              description: t(($) => $.errors.updateFailed),
            })
          },
          onSettled: () => {
            setIsUrlDialogOpen(false)
            onDone?.()
          },
        }
      )
    },
    [assetId, showAlert, updateAssetFromUrl, onInvalidateAsset, refetchRunStatus, t]
  )

  const handleTriggerUpdate = useCallback(async () => {
    if (asset?.source?.sourceWebUrl && urlSchema.safeParse(asset.source.sourceWebUrl).success) {
      onUpdateAssetFromUrl(asset.source.sourceWebUrl, onInvalidateAsset)
    } else {
      setIsUrlDialogOpen(true)
    }
  }, [asset, onUpdateAssetFromUrl, onInvalidateAsset])

  const handleUpdateWithUrlPrompt = useCallback(async () => {
    if (!updateUrl || !urlSchema.safeParse(updateUrl).success) {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.invalidUrl),
        description: t(($) => $.errors.invalidUrlDescription),
      })

      return
    }

    onUpdateAssetFromUrl(updateUrl)
  }, [updateUrl, onUpdateAssetFromUrl, showAlert, t])

  const dialog = (
    <Dialog open={isUrlDialogOpen} onOpenChange={setIsUrlDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Heading1>{t(($) => $.view.updateFromUrlTitle)}</Heading1>
          </DialogTitle>
          <DialogDescription>
            <SectionContent>{t(($) => $.view.updateFromUrlDescription)}</SectionContent>
          </DialogDescription>
        </DialogHeader>
        <Input
          placeholder={t(($) => $.view.updateFromUrlPlaceholder)}
          value={updateUrl}
          onChangeText={setUpdateUrl}
          keyboardType="url"
          autoCapitalize="none"
        />
        <DialogFooter className="mt-6 gap-6">
          <Button
            onPress={handleUpdateWithUrlPrompt}
            disabled={isPending}
            size="lg"
            loading={isPending}
            variant="primary">
            <ButtonLabel>{t(($) => $.view.saveAndUpdate)}</ButtonLabel>
          </Button>
          <Button variant="ghost" onPress={() => setIsUrlDialogOpen(false)} disabled={isPending}>
            {t(($) => $.common.navigation.previous)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return {
    dialog,
    isUrlDialogOpen,
    setIsUrlDialogOpen,
    handleTriggerUpdate,
    handleUpdateWithUrlPrompt,
    isPending: isPending || isPolling || runStatus === 'PENDING',
  }
}
